import Washi from 'washi';
import NumberStepper from './number-stepper';
import { calculateGuestUsed } from './ticket-quantity-limits';

function parseStepperValue(stepper) {
  let value = parseInt(stepper.ui.input[0].value, 10);
  if (isNaN(value)) {
    console.warn(
      'Expected a numeric value for ticket quantity, received: ',
      stepper.ui.input[0].value,
    );
    value = 0;
  }
  return value;
}

function sumStepperValues(steppers) {
  return steppers.reduce(
    (total, stepper) => total + parseStepperValue(stepper),
    0,
  );
}

var TicketNumberStepper = {
  el: '[data-component="ticket-number-stepper"]',

  ui: {
    inputs: '[data-number-stepper="input"]',
    steppers: '[data-ticket-quantity-node]',
  },

  initialize: function () {
    let sharedSteppers = [];
    Washi(NumberStepper, { el: '[data-ticket-quantity-node]' }).map((n) => {
      sharedSteppers.push(n);
    });

    let guestOnlySteppers = [];
    Washi(NumberStepper, {
      el: '[data-guest-pool-node]:not([data-ticket-quantity-node])',
    }).map((n) => {
      guestOnlySteppers.push(n);
    });

    let sectionOnlySteppers = [];
    Washi(NumberStepper, {
      el: '[data-section-limit]:not([data-ticket-quantity-node]):not([data-guest-pool-node])',
    }).map((n) => {
      sectionOnlySteppers.push(n);
    });

    this.sharedSteppers = sharedSteppers;
    this.guestOnlySteppers = guestOnlySteppers;
    this.sectionOnlySteppers = sectionOnlySteppers;
    this.guestPoolSteppers = sharedSteppers
      .filter((s) => s.hasGuestPool)
      .concat(guestOnlySteppers);
    this.sectionLimitSteppers = sharedSteppers
      .filter((s) => s.hasSectionLimit)
      .concat(guestOnlySteppers.filter((s) => s.hasSectionLimit))
      .concat(sectionOnlySteppers);
    this.steppers = sharedSteppers
      .concat(guestOnlySteppers)
      .concat(sectionOnlySteppers);

    this.steppers.forEach((stepper) => {
      stepper.listen(this.handleStepperChange, this);
    });
    // Need to call it at initialization in case of a refresh with values already set
    this.handleStepperChange();
  },

  handleStepperChange: function () {
    if (this.guestPoolSteppers.length > 0) {
      this.updateGuestPoolSteppers();
      this.updateNonGuestSharedSteppers();
      this.updateSectionOnlySteppers();
      return;
    }

    if (this.sectionLimitSteppers.length > 0) {
      this.updateSectionLimitSteppers();
      this.updateNonGuestSharedSteppers();
      return;
    }

    this.updateSharedTotalSteppers(this.sharedSteppers);
  },

  updateSharedTotalSteppers: function (steppers) {
    const totalTicketQuantity = sumStepperValues(steppers);
    steppers.forEach((stepper) => {
      if (
        stepper._updateStateFromTotalTicketQuantity &&
        typeof stepper._updateStateFromTotalTicketQuantity === 'function'
      ) {
        stepper._updateStateFromTotalTicketQuantity(totalTicketQuantity);
      } else {
        console.warn(
          'Expected a function named _updateStateFromTotalTicketQuantity on NumberStepper, received: ',
          stepper,
        );
      }
    });
  },

  updateNonGuestSharedSteppers: function () {
    const nonGuestShared = this.sharedSteppers.filter(
      (s) => !s.hasGuestPool && !s.hasSectionLimit,
    );
    if (nonGuestShared.length > 0) {
      this.updateSharedTotalSteppers(nonGuestShared);
    }
  },

  updateGuestPoolSteppers: function () {
    const guestSharedSteppers = this.sharedSteppers.filter(
      (s) => s.hasGuestPool,
    );
    const sharedTotal = sumStepperValues(guestSharedSteppers);
    const sharedTypeMax =
      guestSharedSteppers.length > 0
        ? guestSharedSteppers[0].state.typeMax
        : null;

    const guestUsed = calculateGuestUsed({
      sharedTotal: sharedTotal,
      sharedTypeMax: sharedTypeMax,
      soloTotals: this.guestOnlySteppers.map((stepper) => ({
        value: parseStepperValue(stepper),
        typeMax: stepper.state.typeMax,
      })),
    });

    const guestLimit = this.guestPoolSteppers[0].state.guestMax || 0;
    const guestRemaining = guestLimit - guestUsed;

    const sectionLimit = this.sectionLimitFor(this.guestPoolSteppers);
    const sectionTotal =
      sectionLimit != null ? sumStepperValues(this.sectionLimitSteppers) : null;

    guestSharedSteppers.forEach((stepper) => {
      stepper._updateStateFromGuestPool({
        groupTotal: sharedTotal,
        guestRemaining: guestRemaining,
        sectionTotal: sectionTotal,
        sectionLimit: sectionLimit,
      });
    });

    this.guestOnlySteppers.forEach((stepper) => {
      stepper._updateStateFromGuestPool({
        groupTotal: parseStepperValue(stepper),
        guestRemaining: guestRemaining,
        sectionTotal: sectionTotal,
        sectionLimit: sectionLimit,
      });
    });
  },

  updateSectionLimitSteppers: function () {
    const sectionLimit = this.sectionLimitFor(this.sectionLimitSteppers);
    if (sectionLimit == null) {
      return;
    }

    const sectionTotal = sumStepperValues(this.sectionLimitSteppers);
    const sharedWithSection = this.sharedSteppers.filter(
      (s) => s.hasSectionLimit,
    );

    if (sharedWithSection.length > 0) {
      const sharedTotal = sumStepperValues(sharedWithSection);
      const typeMax = sharedWithSection[0].state.typeMax;
      sharedWithSection.forEach((stepper) => {
        const value = parseStepperValue(stepper);
        stepper.state.canIncrement =
          sharedTotal < typeMax &&
          sectionTotal < sectionLimit &&
          (stepper.state.max == null || value < stepper.state.max);
        stepper.state.canDecrement = value > 0;
        stepper.render();
      });
    }

    this.sectionOnlySteppers.forEach((stepper) => {
      stepper._updateStateFromSectionLimit({
        sectionTotal: sectionTotal,
        sectionLimit: sectionLimit,
      });
    });
  },

  updateSectionOnlySteppers: function () {
    if (this.sectionOnlySteppers.length === 0) {
      return;
    }

    const sectionLimit = this.sectionLimitFor(this.sectionLimitSteppers);
    if (sectionLimit == null) {
      return;
    }

    const sectionTotal = sumStepperValues(this.sectionLimitSteppers);
    this.sectionOnlySteppers.forEach((stepper) => {
      stepper._updateStateFromSectionLimit({
        sectionTotal: sectionTotal,
        sectionLimit: sectionLimit,
      });
    });
  },

  sectionLimitFor: function (steppers) {
    const withLimit = steppers.find((s) => s.state.sectionLimit != null);
    return withLimit ? withLimit.state.sectionLimit : null;
  },
};

export default TicketNumberStepper;
